<template>
  <div class="login-wrapper">
    <div class="login-container">
      <div class="login-form-side">
        <div class="login-logo"><i class="fas fa-heartbeat"></i> 益体康</div>
        <div class="login-welcome">欢迎登录 · 心贴智慧工作台</div>
        <div class="login-input-group">
          <label><i class="far fa-user"></i> 账号</label>
          <input type="text" v-model="username" class="login-input-field" placeholder="手机号" @keyup.enter="login">
        </div>
        <div class="login-input-group">
          <label><i class="fas fa-lock"></i> 密码</label>
          <input type="password" v-model="pwd" class="login-input-field" placeholder="密码" @keyup.enter="login">
        </div>
        <div class="form-options">
          <el-checkbox v-model="checked">记住用户名和密码</el-checkbox>
        </div>
        <button class="login-btn" @click="login" :disabled="loginDisabled">
          <i class="fas fa-arrow-right-to-bracket"></i> {{ loginDisabled ? '登录中...' : '登录系统' }}
        </button>
        <p class="agreement-text">登录即代表同意<a href="/static/agreement.html" target="_blank">用户协议</a>和<a href="/static/privacy.html" target="_blank">隐私政策</a></p>
        <div class="demo-tip"><i class="fas fa-info-circle"></i> 版本号 V2.8.2</div>
      </div>
      <div class="login-hero-side">
        <div class="ecg-container">
          <canvas id="rollingECG" width="650" height="220" style="width:100%; height:auto; max-width:650px; aspect-ratio:650/220;"></canvas>
        </div>
        <div class="hero-quote">
          <h3>智能心电</h3>
          <p>动态监测 | 远程报告 | 数据可视</p>
        </div>
      </div>
    </div>

    <p class="footer-copyright">
      <i class="far fa-copyright"></i> 2007-{{ currentYear }} ETComm 益体康（北京）科技有限公司 京ICP备09007401号 京公网安备11010802027358号
      <a @click="openDownloadDialog" style="cursor: pointer;">
        <i class="fas fa-download"></i> 报告批量下载工具 v2.8.0
      </a>
    </p>

    <!-- 下载工具选择弹窗 -->
    <DownloadDialog :show.sync="showDownloadDialog" />
  </div>
</template>

<script>
import ApiService from "../service/ApiService";
import pub from "../service/public";
import cookie from "../service/cookie";
import md5 from "js-md5";
import DownloadDialog from "./DownloadDialog.vue";

// 动态滚动心电图类
class RollingECG {
  constructor(canvasId, width = 650, height = 220) {
    this.canvas = document.getElementById(canvasId);
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;

    // 存储波形数据点（每个像素一个点）
    this.points = new Array(this.width);
    this.baseline = this.height / 2;

    // 心电模拟参数
    this.phase = 0;
    this.phaseStep = 0.048;   // 控制心率速度

    // 初始化时生成一段完整的波形，避免一开始出现直线
    this.initWaveform();

    this.animationId = null;
    this.isRunning = false;
  }

  // 生成单个心电图数值（归一化振幅 -1..1）
  getECGValue(phase) {
    let t = phase % (Math.PI * 2);
    let val = 0;
    let x = t / (Math.PI * 2); // 0-1 周期位置

    // P波
    if (x >= 0.05 && x <= 0.18) {
      let p = (x - 0.05) / 0.13;
      val = 0.09 * Math.sin(p * Math.PI);
    }
    // QRS波群
    else if (x >= 0.2 && x <= 0.38) {
      let qrs = (x - 0.2) / 0.18;
      if (qrs < 0.15) {
        val = -0.12 * (qrs / 0.15);
      } else if (qrs < 0.4) {
        let r = (qrs - 0.15) / 0.25;
        val = -0.12 + 0.85 * r;
      } else if (qrs < 0.65) {
        let s = (qrs - 0.4) / 0.25;
        val = 0.73 - 1.05 * s;
        if (val < -0.28) val = -0.28;
      } else {
        let rest = (qrs - 0.65) / 0.35;
        val = -0.28 * (1 - rest);
      }
    }
    // ST段
    else if (x >= 0.38 && x <= 0.55) {
      val = 0.02 * Math.sin((x - 0.38) / 0.17 * Math.PI);
    }
    // T波
    else if (x >= 0.55 && x <= 0.78) {
      let tw = (x - 0.55) / 0.23;
      val = 0.24 * Math.sin(tw * Math.PI);
    }
    else {
      val = (Math.random() - 0.5) * 0.01;
    }
    // 限制
    if (val > 0.4) val = 0.4;
    if (val < -0.35) val = -0.35;
    return val;
  }

  // 初始化波形数据（非直线，根据当前相位生成）
  initWaveform() {
    // 先填充基线，然后快速更新多次产生波形
    for (let i = 0; i < this.width; i++) {
      this.points[i] = this.baseline;
    }
    // 快速向前演进50步，使波形脱离直线状态
    for (let step = 0; step < 600; step++) {
      this.update();
    }
  }

  update() {
    this.phase += this.phaseStep;
    if (this.phase > Math.PI * 2) this.phase -= Math.PI * 2;

    let norm = this.getECGValue(this.phase);
    let amplitude = this.height * 0.4;   // 放大振幅
    let newY = this.baseline - norm * amplitude;
    newY = Math.min(Math.max(newY, 8), this.height - 8);

    // 整体左移一个像素
    for (let i = 0; i < this.width - 1; i++) {
      this.points[i] = this.points[i + 1];
    }
    this.points[this.width - 1] = newY;
  }

  draw() {
    if (!this.ctx) return;
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.fillStyle = '#fffef7';
    this.ctx.fillRect(0, 0, this.width, this.height);

    // 绘制波形
    this.ctx.beginPath();
    this.ctx.strokeStyle = '#2c9b86';
    this.ctx.lineWidth = 2.2;
    this.ctx.lineJoin = 'round';
    this.ctx.lineCap = 'round';

    let first = true;
    for (let x = 0; x < this.width; x++) {
      let y = this.points[x];
      if (first) {
        this.ctx.moveTo(x, y);
        first = false;
      } else {
        this.ctx.lineTo(x, y);
      }
    }
    this.ctx.stroke();
  }

  animate() {
    this.update();
    this.draw();
    this.animationId = requestAnimationFrame(() => this.animate());
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.animate();
  }

  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.isRunning = false;
  }

  reset() {
    // 重置数据，但重新生成波形，避免直线
    this.initWaveform();
    this.draw();
  }
}

export default {
  name: "LoginModern",
  components: {
    DownloadDialog
  },
  data() {
    return {
      username: "",
      pwd: "",
      checked: false,
      loginDisabled: false,
      currentYear: new Date().getFullYear(),
      ecgAnimation: null,
      configVersion: 0,
      showDownloadDialog: false
    }
  },
  computed: {
    downloadUrl() {
      // 使用configVersion作为依赖，确保computed能响应更新
      this.configVersion;
      return localStorage.getItem('exeDownLoadUrl') || '';
    }
  },
  created() {
    let checked = this.$cookies.get('28project_userChecked');

    if (checked == 'true') {
      let name = this.$cookies.get("28project_username");
      let pwd = this.$cookies.get("28project_userpwd");
      this.username = name;
      this.pwd = pwd;
      this.checked = true;
    }

    // 监听配置加载完成事件
    window.addEventListener('configLoaded', () => {
      this.configVersion++;
    });
  },
  mounted() {
    this.initECG();
  },
  beforeDestroy() {
    if (this.ecgAnimation) {
      this.ecgAnimation.stop();
    }
  },
  methods: {
    initECG() {
      this.ecgAnimation = new RollingECG('rollingECG', 650, 220);
      this.ecgAnimation.start();
    },
    login() {
      let that = this;
      this.username = this.username.trim();
      this.pwd = this.pwd.trim();
      if (that.username == "") {
        this.$message({showClose: true, message: '用户名不能为空', type: 'warning', duration: 1500});
      } else if (that.pwd == "") {
        that.$message({showClose: true, message: '密码不能为空', type: 'warning', duration: 1500});
      } else {
        that.handleLogin();
      }
    },
    handleLogin() {
      this.loginDisabled = true;
      let that = this;
      const params = {
        page: {number: 0, limit: 1},
        keyWord: [],
        parms: {strUsrName: this.username, strUsrPwd: md5(this.pwd), strClientUuid: 'A0F9GQHU6YE3D5AG'}
      };

      ApiService.UsrLogin(params)
        .then(res => {
          if (res.code === pub.backCode.SUCCESS) {
            that.handleLoginSuccess(res);
          } else {
            pub.analysisReturnCode(res, null);
          }
        })
        .catch(error => {
          console.error('登录错误：', error);
          pub.tip('登录失败，请检查网络连接', 'error');
        })
        .finally(() => {
          this.loginDisabled = false;
        });
    },
    handleLoginSuccess(res) {
      let userInfo = res.dic;
      // 登录态以 localStorage 为权威源、cookie 作镜像（saveLoginState 双写）
      pub.saveLoginState(userInfo.strAuthorization, userInfo.strRefreshToken);
      cookie.setCookie('28project_UsrInfo', JSON.stringify(userInfo));
      this.$cookies.set('28project_username', this.username);

      if (this.checked) {
        this.$cookies.set('28project_userpwd', this.pwd);
        this.$cookies.set('28project_userChecked',true);
      } else {
        this.$cookies.remove('28project_userpwd');
        this.$cookies.remove('28project_userChecked');
      }
      this.loadSystemConst();
    },
    loadSystemConst() {
      let that = this;
      let params = {page: {number: 0, limit: 500}, keyWord: [], parms: {}};
      ApiService.GetConst(params).then(res => {
        if (res.code === pub.backCode.SUCCESS && res.data) {
          cookie.setCookie('28Project_Const', JSON.stringify(res.data));
          // 用 replace 而非 push：登录页从历史栈移除，浏览器后退不会回到登录页
          that.$router.replace('/home');
        } else {
          console.warn('获取系统枚举常量失败:', res.message || res.msg);
        }
      }).catch(error => {
        console.error('获取枚举常量错误：', error);
      });
    },
    trackDownloadTool(version) {
      // umami行为统计
      if (typeof umami !== 'undefined') {
        const url = this.downloadUrl;
        umami.track('downloadExe', {
          version: 'v2.8.0',
          os: version,
          url: url
        });
      }
    },
    openDownloadDialog() {
      this.showDownloadDialog = true;
    }

  }
}
</script>

<style scoped lang="scss">
.login-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 95vh;
}

.form-options {
  margin-bottom: 16px;
  text-align: left;
}

>>>.el-checkbox__label {
  color: #2c6b5e !important;
  font-size: 0.8rem;
}

>>>.el-checkbox__input.is-checked + .el-checkbox__label {
  color: #2c9b86 !important;
}

>>>.el-checkbox__input.is-checked .el-checkbox__inner {
  background: #2c9b86;
  border-color: #2c9b86;
}

.agreement-text {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin: 12px 0;

  a {
    color: #2c9b86;
    text-decoration: none;
    margin: 0 4px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.ecg-container {
  width: 100%;
  max-width: 650px;
  margin-bottom: 28px;
  background: #fffef7;
  border-radius: 28px;
  box-shadow: 0 12px 24px rgba(0,0,0,0.05);
  padding: 12px;
}

.footer-tool a {
  color: #5f8c7e;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #2c9b86;
  }
}

.ecg-path {
  stroke: #2c9b86;
  stroke-width: 2;
  fill: none;
  stroke-dasharray: 300;
  stroke-dashoffset: 300;
  animation: drawLine 2s ease-in-out infinite;
}

@keyframes drawLine {
  0% {
    stroke-dashoffset: 300;
  }
  80% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: 0;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Login.vue